import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { EmployeeConsultantsService } from '../../../../../services/api/hr/employee-consultants.service';
import { AlertInfo } from '../../../../../models/alert-info';
import { SavedDataInfo } from '../../../../../models/form/saved-data-info';
import { InputFieldComponent } from '../../../../../components/form/input-field/input-field.component';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';

@Component({
  selector: 'employee-documents',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbAlert,
    InputFieldComponent,
    LoaderComponent
  ],
  templateUrl: './employee-documents.component.html',
  styleUrl: './employee-documents.component.scss'
})
export class EmployeeDocumentsComponent implements OnInit {
  @Input() employeeId: number | undefined;
  @Input() readonly: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  editForm!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: 'success', message: '' };
  loading: boolean = false;
  documents: any[] = [];
  selectedFile: File | null = null;
  fileName: string = 'No file selected';
  documentToDelete: any = null;
  documentToEdit: any = null;
  showUploadForm: boolean = false;
  showEditForm: boolean = false;

  constructor(
    private fb: FormBuilder,
    private employeeConsultantsService: EmployeeConsultantsService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.initForm();
    setTimeout(() => {
      console.log(this.employeeId);
    }, 1000);
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (this.employeeId) {             
      this.loadDocuments();
    }
    }, 700);
  }

  private initForm(): void {
    this.form = this.fb.group({
      title: ['', Validators.required]
    });

    this.editForm = this.fb.group({
      title: ['', Validators.required]
    });

    if (this.readonly) {
      this.form.disable();
      this.editForm.disable();
    }
  }

  loadDocuments(): void {
    if (!this.employeeId) return;

    this.loading = true;
    this.employeeConsultantsService.listEmployeeDocuments(this.employeeId).subscribe({
      next: (response) => {
        this.documents = response.data.employeeDocuments || [];
        this.loading = false;
      },
      error: (error) => {
        this.showAlertInfo('danger', 'Error retrieving documents: ' + error.message);
        this.loading = false;
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.fileName = file.name;
    } else {
      this.selectedFile = null;
      this.fileName = 'No file selected';
    }
  }

  toggleUploadForm(): void {
    this.showUploadForm = !this.showUploadForm;
    this.showEditForm = false;
    if (!this.showUploadForm) {
      this.resetForm();
    }
  }

  onSubmit(): void {
    if (this.form.invalid || !this.selectedFile || !this.employeeId) {
      this.form.markAllAsTouched();
      if (!this.selectedFile) {
        this.showAlertInfo('danger', 'Please select a file to upload');
      }
      return;
    }

    this.loading = true;
    
    const data = {
      title: this.form.get('title')?.value
    };
    
    const formData = this.createFormDataForUpload(this.selectedFile, data);
    
    this.employeeConsultantsService.createEmployeeDocuments(this.employeeId, formData).subscribe({
      next: (response) => {
        this.showAlertInfo('success', 'Document uploaded successfully');
        this.loadDocuments();
        this.resetForm();
        this.loading = false;
        this.onSavedData.emit({ success: true });
      },
      error: (error) => {
        const errorMessage = error.error?.message || error.message || 'Unknown error';
        this.showAlertInfo('danger', 'Error uploading document: ' + errorMessage);
        console.error('Upload error details:', error);
        this.loading = false;
      }
    });
  }

  deleteDocument(document: any): void {
    this.documentToDelete = document;
    this.confirmationDialogService.open(
      `Are you sure you want to delete the document "${document.title}"?`,
      () => this.onConfirmDelete()
    );
  }

  onConfirmDelete(): void {
    if (!this.documentToDelete || !this.employeeId) return;

    this.loading = true;
    this.employeeConsultantsService.deleteEmployeeDocuments(this.employeeId, this.documentToDelete.employeeDocumentId).subscribe({
      next: () => {
        this.alertNotificationService.success('Document deleted successfully');
        this.showAlertInfo('success', 'Document deleted successfully');
        this.loadDocuments();
        this.loading = false;
      },
      error: (error) => {
        this.alertNotificationService.error('Error deleting document: ' + (error.error?.message || error.message || 'Unknown error'));
        this.showAlertInfo('danger', 'Error deleting document: ' + (error.error?.message || error.message || 'Unknown error'));
        console.error('Delete error details:', error);
        this.loading = false;
      }
    });
    
    this.documentToDelete = null;
  }

  downloadDocument(document: any): void {
    if (document.length > 0) {
      this.employeeConsultantsService.GET(document[0].url).subscribe({
        next: (response) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }
      })
    } else {
      this.alertNotificationService.warning('File URL not available');
    }
  }

  resetForm(): void {
    this.form.reset();
    this.selectedFile = null;
    this.fileName = 'No file selected';
    this.showUploadForm = false;
    this.showEditForm = false;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  getObjectControl(): FormControl {
    return this.form.get('title') as FormControl;
  }
  
  getEditTitleControl(): FormControl {
    return this.editForm.get('title') as FormControl;
  }

  createFormDataForUpload(file: File, data: any): FormData {
    const formData = new FormData();
    const dataJSON = JSON.stringify(data);

    formData.append('file', file, file.name);
    formData.append('data', dataJSON);
    
    return formData;
  }

  editDocument(document: any): void {
    this.documentToEdit = document;
    this.editForm.patchValue({
      title: document.title
    });
    this.showEditForm = true;
    this.showUploadForm = false;
  }

  cancelEdit(): void {
    this.documentToEdit = null;
    this.editForm.reset();
    this.showEditForm = false;
  }

  saveEdit(): void {
    if (this.editForm.invalid || !this.documentToEdit || !this.employeeId) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    
    const data = {
      title: this.editForm.get('title')?.value
    };
    
    this.employeeConsultantsService.updateEmployeeDocuments(
      this.employeeId, 
      this.documentToEdit.employeeDocumentId, 
      data
    ).subscribe({
      next: (response) => {
        this.showAlertInfo('success', 'Document updated successfully');
        this.loadDocuments();
        this.cancelEdit();
        this.loading = false;
        this.onSavedData.emit({ success: true });
      },
      error: (error) => {
        const errorMessage = error.error?.message || error.message || 'Unknown error';
        this.showAlertInfo('danger', 'Error updating document: ' + errorMessage);
        console.error('Update error details:', error);
        this.loading = false;
      }
    });
  }
}


